import { useEffect, useState } from 'react';
import type { DocumentResume } from '@rh/shared';
import { AVERTISSEMENT_DOC, formeApercu } from '@rh/shared';
import { appel, chargerDocument } from '../api';
import { ApercuPdf } from './ApercuPdf';
import { ApercuWord } from './ApercuWord';

/**
 * Lecteur de CV : le PDF est dessiné page par page (lisible sur téléphone),
 * le Word converti en HTML dans un cadre isolé, l'image affichée telle
 * quelle. Le texte extrait sert à la lecture rapide et aux formats sans
 * aperçu. Tout binaire est chargé avec le jeton : un `src` direct ne pourrait
 * pas s'authentifier.
 */
export function LecteurDocument({ document }: { document: DocumentResume }) {
  const [texte, setTexte] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ongletTexte, setOngletTexte] = useState(false);
  const [pleinEcran, setPleinEcran] = useState(false);

  // La forme d'aperçu se déduit de l'extension enregistrée, comme côté API :
  // le type annoncé par le message d'origine n'est pas une source de vérité.
  const forme = formeApercu(document.nomFichier);
  const apercuPossible = forme === 'pdf' || forme === 'image' || forme === 'word';

  useEffect(() => {
    setTexte(null);
    setOngletTexte(false);
    if (!document.aDuTexte) return;
    let vivant = true;
    appel<{ texte: string }>(`/documents/${document.id}/texte`)
      .then((reponse) => vivant && setTexte(reponse.texte))
      .catch(() => vivant && setTexte(null));
    return () => {
      vivant = false;
    };
  }, [document.id, document.aDuTexte]);

  useEffect(() => {
    setUrl(null);
    setErreur(null);
    if (forme !== 'image') return;
    let objet: string | null = null;
    chargerDocument(document.id)
      .then((lien) => {
        objet = lien;
        setUrl(lien);
      })
      .catch((e: Error) => setErreur(e.message));
    return () => {
      if (objet) URL.revokeObjectURL(objet);
    };
  }, [document.id, forme]);

  useEffect(() => {
    if (!pleinEcran) return;
    const fermer = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') setPleinEcran(false);
    };
    window.addEventListener('keydown', fermer);
    return () => window.removeEventListener('keydown', fermer);
  }, [pleinEcran]);

  // Sans aperçu possible, le texte extrait est la seule lecture utile.
  const afficherTexte = ongletTexte || !apercuPossible;
  const titre = `Document ${document.nomFichier}`;

  return (
    <div className={pleinEcran ? 'lecteur plein-ecran' : 'lecteur'}>
      <div className="onglets">
        <button
          type="button"
          className={afficherTexte ? '' : 'actif'}
          onClick={() => setOngletTexte(false)}
          disabled={!apercuPossible}
        >
          Document
        </button>
        <button
          type="button"
          className={afficherTexte ? 'actif' : ''}
          onClick={() => setOngletTexte(true)}
          disabled={!document.aDuTexte}
        >
          Texte extrait{document.ocr ? ' (OCR)' : ''}
        </button>
        <button
          type="button"
          className="plein"
          aria-pressed={pleinEcran}
          onClick={() => setPleinEcran((actuel) => !actuel)}
        >
          {pleinEcran ? 'Fermer' : 'Plein écran'}
        </button>
      </div>

      {erreur && <p className="erreur">{erreur}</p>}

      {afficherTexte ? (
        <pre className="texte-extrait">
          {texte ??
            (/\.doc$/i.test(document.nomFichier)
              ? `Aucun aperçu d’un ancien document Word : téléchargez-le. ${AVERTISSEMENT_DOC}.`
              : 'Aucun texte extrait : ouvrir le document pour le lire.')}
        </pre>
      ) : forme === 'pdf' ? (
        <ApercuPdf key={document.id} documentId={document.id} />
      ) : forme === 'word' ? (
        <ApercuWord key={document.id} documentId={document.id} titre={titre} />
      ) : url ? (
        <img className="apercu" src={url} alt={document.nomFichier} />
      ) : (
        <p className="attente">Chargement du document…</p>
      )}
    </div>
  );
}
